import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CategorySubcategoryPicker from "@/components/CategorySubcategoryPicker";
import { toast } from "sonner";
import { Wand2, Copy, MailCheck, KeyRound, Eye, EyeOff } from "lucide-react";

export default function AdminCreatePro() {
  const [form, setForm] = useState({
    email: "", password: "", first_name: "", last_name: "", phone: "",
    business_name: "", category_id: "", subcategory_id: "", address: "", city: "", postal_code: "",
    website: "", opening_hours: "", coverage: "local",
  });
  const [autoPassword, setAutoPassword] = useState(true);
  const [showPwd, setShowPwd] = useState(false);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(null); // { email, password } after submit
  const navigate = useNavigate();
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target?.value ?? e }));

  useEffect(() => {
    api.get("/admin/cities").then((r) => setCities(r.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (autoPassword) payload.password = "";  // backend generates
      const { data } = await api.post("/admin/professionals", payload);
      if (data.password_generated) {
        setGenerated({ email: form.email, password: data.password });
        toast.success("Compte créé — identifiants envoyés par email au pro");
      } else {
        toast.success("Professionnel créé");
        navigate(`/admin/offres/nouvelle?business=${data.business_id}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const copyCreds = async () => {
    if (!generated) return;
    try {
      await navigator.clipboard.writeText(`${generated.email} / ${generated.password}`);
      toast.success("Identifiants copiés");
    } catch {}
  };

  if (generated) {
    return (
      <div className="max-w-2xl">
        <div className="bg-white border border-emerald-200 rounded-2xl p-6" data-testid="admin-pro-created-panel">
          <div className="flex items-center gap-2">
            <MailCheck className="text-emerald-600" size={22}/>
            <h1 className="font-heading text-2xl font-bold">Compte pro créé & email envoyé</h1>
          </div>
          <p className="text-sm text-slate-600 mt-2">
            Un email avec les identifiants a été envoyé à <strong>{generated.email}</strong>.
            Conservez ces identifiants en cas de besoin :
          </p>
          <div className="mt-4 p-4 bg-slate-900 text-emerald-300 rounded-xl font-mono text-sm flex items-center justify-between">
            <div>
              <div><span className="text-slate-400">Email :</span> {generated.email}</div>
              <div className="mt-1"><span className="text-slate-400">Mot de passe :</span> {generated.password}</div>
            </div>
            <Button type="button" variant="outline" onClick={copyCreds} className="bg-white text-slate-900" data-testid="copy-creds-btn">
              <Copy size={16} className="mr-1"/> Copier
            </Button>
          </div>
          <div className="mt-6 flex gap-3">
            <Button onClick={()=>navigate("/admin/offres/nouvelle")} className="btn-shine text-white">
              Créer une offre pour ce pro
            </Button>
            <Button variant="outline" onClick={()=>navigate("/admin/professionnels")}>Retour à la liste</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold" data-testid="admin-create-pro-title">Créer un professionnel</h1>
      <p className="text-sm text-slate-500 mt-1">L'entreprise sera activée immédiatement. Vous pourrez ensuite créer une offre pour ce commerce.</p>
      <form onSubmit={submit} className="mt-6 bg-white border border-slate-200 rounded-2xl p-6 space-y-4 max-w-3xl" data-testid="admin-create-pro-form">
        <div className="text-sm font-semibold text-slate-700">Compte professionnel</div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Prénom</Label><Input required value={form.first_name} onChange={set("first_name")} data-testid="pro-first"/></div>
          <div><Label>Nom</Label><Input required value={form.last_name} onChange={set("last_name")} data-testid="pro-last"/></div>
          <div><Label>Email</Label><Input type="email" required value={form.email} onChange={set("email")} data-testid="pro-email"/></div>
          <div><Label>Téléphone</Label><Input value={form.phone} onChange={set("phone")}/></div>
        </div>

        <div className="p-3 rounded-xl border border-slate-200 bg-orange-50/40">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wand2 size={18} className="text-[#E05638]"/>
              <div>
                <div className="font-semibold text-sm">Générer un mot de passe sécurisé</div>
                <div className="text-xs text-slate-500">
                  {autoPassword
                    ? "Un mot de passe fort sera créé et envoyé par email au pro."
                    : "Vous saisissez vous-même le mot de passe (min 6 caractères)."}
                </div>
              </div>
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={autoPassword} onChange={(e)=>setAutoPassword(e.target.checked)} data-testid="auto-password-toggle" className="w-4 h-4"/>
              <span className="text-sm font-semibold">{autoPassword ? "Auto" : "Manuel"}</span>
            </label>
          </div>
          {!autoPassword && (
            <div className="mt-3">
              <Label className="flex items-center gap-1"><KeyRound size={12}/> Mot de passe (min 6)</Label>
              <div className="relative">
                <Input type={showPwd ? "text" : "password"} required={!autoPassword} minLength={6} value={form.password} onChange={set("password")} className="pr-10" data-testid="pro-password"/>
                <button type="button" onClick={()=>setShowPwd(v=>!v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                        aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                        data-testid="pro-password-toggle">
                  {showPwd ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="pt-3 border-t border-slate-100 space-y-3">
          <div className="text-sm font-semibold text-slate-700">Entreprise</div>
          <div><Label>Nom de l'entreprise</Label><Input required value={form.business_name} onChange={set("business_name")} data-testid="biz-name"/></div>
          <CategorySubcategoryPicker
            value={{ category_id: form.category_id, subcategory_id: form.subcategory_id }}
            onChange={({ category_id, subcategory_id }) =>
              setForm((s) => ({ ...s, category_id, subcategory_id }))}
            testIdPrefix="admin-pro"
            required
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Ville</Label>
              <Select value={form.city} onValueChange={(v)=>setForm((s)=>({...s, city: v}))}>
                <SelectTrigger><SelectValue placeholder="Choisir"/></SelectTrigger>
                <SelectContent>{cities.map((c)=>(<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div><Label>Code postal</Label><Input value={form.postal_code} onChange={set("postal_code")}/></div>
            <div><Label>Adresse</Label><Input value={form.address} onChange={set("address")} placeholder="12 rue de la Poste"/></div>
            <div><Label>Site web</Label><Input value={form.website} onChange={set("website")}/></div>
            <div><Label>Horaires</Label><Input value={form.opening_hours} onChange={set("opening_hours")}/></div>
          </div>
          <p className="text-xs text-slate-500">💡 L'adresse et la ville seront automatiquement géocodées pour afficher le commerce sur la carte Leaflet.</p>
          <div>
            <Label>Zone d'intervention</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <button type="button" onClick={()=>setForm((s)=>({...s, coverage: "local"}))}
                      className={`p-3 rounded-xl border text-left text-sm ${form.coverage==="local"?"border-[#E05638] bg-orange-50":"border-slate-200 bg-white"}`}>
                <div className="font-semibold">Ville / pôle</div>
              </button>
              <button type="button" onClick={()=>setForm((s)=>({...s, coverage: "department"}))}
                      className={`p-3 rounded-xl border text-left text-sm ${form.coverage==="department"?"border-[#E05638] bg-orange-50":"border-slate-200 bg-white"}`}>
                <div className="font-semibold">Tout l'Eure-et-Loir</div>
              </button>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <Button type="submit" disabled={loading} className="btn-shine text-white" data-testid="admin-create-pro-submit">
            {loading ? "Création…" : (autoPassword ? "Créer & envoyer identifiants" : "Créer et publier une offre")}
          </Button>
          <Button type="button" variant="outline" onClick={()=>navigate("/admin/professionnels")}>Annuler</Button>
        </div>
      </form>
    </div>
  );
}
